/**
 * No Relief — counters about to go unmanned, grouped by the time it happens,
 * with a tick for each officer who has been told.
 *
 * Each counter is a pill with a checkbox:
 * - red, unticked: nobody has told the officer there is no relief yet;
 * - struck through, ticked: that call was made (kept as a record — untick
 *   the latest one to undo it, e.g. after an accidental tap);
 * - green, unticked: they were told "no relief", but an edit has since put
 *   someone on the counter — call again to say relief is coming.
 * A point nobody called about just disappears once it gets relief. One that
 * cycles through relief and back to no-relief with no call made in between
 * shows a fresh, unticked "no relief" pill again — that intervening relief
 * was never announced, so the earlier tick doesn't cover this occurrence.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { formatCounterLabel } from '@/config/modeConfig';
import { slotToHhmm } from '@/utils/partialTiming';
import { ViewOnlyBanner } from '@/components/ViewOnlyBanner';
import type { NoReliefAck, NoReliefAckKind } from '@/types/api';
import type { NoReliefEntry, NoReliefGroup } from '@/utils/noRelief';
import { UI_SCALE, popupMaxH } from '@/utils/uiScale';

const PILL_COLORS: Record<NoReliefAckKind, { bg: string; border: string; color: string }> = {
  no_relief: { bg: '#fee2e2', border: '#f87171', color: '#991b1b' },
  relief: { bg: '#dcfce7', border: '#22c55e', color: '#166534' },
};

const DONE_COLORS = { bg: '#f1f5f9', border: '#cbd5e1', color: '#64748b' };

function ackTitle(ack: NoReliefAck, counterLabel: string): string {
  const what = ack.kind === 'no_relief' ? 'no relief' : 'relief is coming';
  const who = ack.acked_by ? ` by ${ack.acked_by}` : '';
  const at = ack.acked_at ? ` at ${new Date(/[zZ]$/.test(ack.acked_at) ? ack.acked_at : `${ack.acked_at}Z`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '';
  return `${counterLabel}: officer told ${what}${who}${at}`;
}

function Pill({
  label,
  kind,
  checked,
  disabled,
  title,
  onCheck,
  onUncheck,
}: {
  label: string;
  kind: NoReliefAckKind;
  checked: boolean;
  /** Disables the checkbox regardless of `checked` — view-only, or busy. */
  disabled: boolean;
  title: string;
  onCheck?: () => void;
  /** Present only on a checked pill the user is allowed to untick (their own
   *  mistake, undoable) — absent (or disabled) leaves it a plain read-only
   *  history pill, same as before. */
  onUncheck?: () => void;
}) {
  const colors = checked ? DONE_COLORS : PILL_COLORS[kind];
  const canToggle = !disabled && (checked ? !!onUncheck : true);
  return (
    <label
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px 3px 6px',
        borderRadius: 999, border: `1px solid ${colors.border}`, background: colors.bg,
        color: colors.color, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
        cursor: canToggle ? 'pointer' : 'default',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={!canToggle}
        onChange={() => { if (checked) onUncheck?.(); else onCheck?.(); }}
        style={{ margin: 0, cursor: 'inherit', accentColor: colors.border }}
      />
      <span style={{ textDecoration: checked ? 'line-through' : 'none' }}>{label}</span>
    </label>
  );
}

function EntryPills({
  entry,
  operationMode,
  canEdit,
  onAck,
  onUndoAck,
}: {
  entry: NoReliefEntry;
  operationMode: string | undefined;
  canEdit: boolean;
  onAck: (entry: NoReliefEntry, kind: NoReliefAckKind) => Promise<void>;
  onUndoAck: (ackId: number) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const counterLabel = formatCounterLabel(entry.counter + 1, operationMode);
  // Only the most recent tick for this counter/slot can be undone — matches
  // the server rule (see backend/db_handlers.py), so the log never gets a
  // hole in the middle of its history.
  const latestAckId = entry.acks[entry.acks.length - 1]?.id;

  const handleCheck = async (kind: NoReliefAckKind) => {
    if (busy) return;
    setBusy(true);
    try {
      await onAck(entry, kind);
    } finally {
      setBusy(false);
    }
  };

  const handleUncheck = async (ackId: number) => {
    if (busy) return;
    setBusy(true);
    try {
      await onUndoAck(ackId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {entry.acks.map((ack) => {
        const isLatest = ack.id === latestAckId;
        return (
          <Pill
            key={ack.id}
            label={ack.kind === 'relief' ? `${counterLabel} · relief` : counterLabel}
            kind={ack.kind}
            checked
            disabled={!canEdit || busy || !isLatest}
            title={
              !canEdit
                ? ackTitle(ack, counterLabel)
                : isLatest
                  ? `${ackTitle(ack, counterLabel)} — tap to undo if this was a mistake`
                  : `${ackTitle(ack, counterLabel)} (an older tick — only the latest can be undone)`
            }
            onUncheck={canEdit && isLatest ? () => void handleUncheck(ack.id) : undefined}
          />
        );
      })}
      {entry.pending && (
        <Pill
          label={entry.pending === 'relief' ? `${counterLabel} · relief now` : counterLabel}
          kind={entry.pending}
          checked={false}
          disabled={!canEdit || busy}
          title={
            !canEdit
              ? 'View only — switch to Edit to tick'
              : entry.pending === 'relief'
                ? `${counterLabel} now has relief — tick once you've told the officer`
                : entry.acks.length > 0
                  ? `${counterLabel} has no relief again — tick once you've told the officer`
                  : `Tick once you've told the officer on ${counterLabel} there is no relief`
          }
          onCheck={() => void handleCheck(entry.pending!)}
        />
      )}
    </span>
  );
}

export function NoReliefModal({
  groups,
  operationMode,
  startHour,
  canEdit,
  onAck,
  onUndoAck,
  onClose,
}: {
  groups: NoReliefGroup[];
  operationMode: string | undefined;
  startHour: number;
  canEdit: boolean;
  onAck: (counter: number, slot: number, kind: NoReliefAckKind) => Promise<void>;
  onUndoAck: (ackId: number) => Promise<void>;
  onClose: () => void;
}) {
  const [error, setError] = useState('');

  const handleAck = async (entry: NoReliefEntry, kind: NoReliefAckKind) => {
    setError('');
    try {
      await onAck(entry.counter, entry.slot, kind);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save the tick.');
    }
  };

  const handleUndoAck = async (ackId: number) => {
    setError('');
    try {
      await onUndoAck(ackId);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not undo that tick.');
    }
  };

  // Portalled to document.body — opened from a button that portals into the
  // tab bar, this modal needs to render regardless of whether its own React
  // parent (HomeTab) sits inside the display:none wrapper App.tsx uses when
  // Settings is the active tab. "position: fixed" doesn't escape a
  // display:none ancestor, so without the portal this modal would be
  // invisible whenever it's opened from a tab other than Counter/Officers.
  return createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        style={{ background: 'white', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          zoom: UI_SCALE, maxHeight: popupMaxH(80) }}
      >
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>🚫 No Relief</span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#888', padding: 0 }}
          >
            ✕
          </button>
        </div>

        {!canEdit && <ViewOnlyBanner compact />}

        <div style={{ padding: '8px 18px', borderBottom: '1px solid #e5e7eb', fontSize: 12, color: '#64748b',
          display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
          <span>Tick = Officer informed</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 999, background: PILL_COLORS.no_relief.bg,
                border: `1px solid ${PILL_COLORS.no_relief.border}` }} />
              no relief
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 999, background: PILL_COLORS.relief.bg,
                border: `1px solid ${PILL_COLORS.relief.border}` }} />
              relief now — call again
            </span>
          </div>
        </div>

        {error && (
          <div style={{ padding: '6px 18px', fontSize: 12, color: '#dc3545', flexShrink: 0 }}>{error}</div>
        )}

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {groups.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
              Every counter has relief.
            </div>
          ) : (
            groups.map((group) => (
              <div
                key={group.slot}
                style={{ display: 'flex', gap: 12, padding: '10px 18px', borderBottom: '1px solid #f1f5f9',
                  alignItems: 'flex-start' }}
              >
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#1e293b',
                  width: 44, flexShrink: 0, paddingTop: 3 }}>
                  {slotToHhmm(group.slot, startHour)}
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1 }}>
                  {group.entries.map((entry) => (
                    <EntryPills
                      key={`${entry.counter}:${entry.slot}`}
                      entry={entry}
                      operationMode={operationMode}
                      canEdit={canEdit}
                      onAck={handleAck}
                      onUndoAck={handleUndoAck}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ padding: '10px 18px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ padding: '7px 16px', background: '#f8f9fa', border: '1px solid #ddd',
              borderRadius: 7, fontSize: 13, cursor: 'pointer' }}
          >
            Back
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
